import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, HelpCircle } from 'lucide-react';

const Auth = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register state
  const [regEmail, setRegEmail] = useState('');
  const [regUsername, setRegUsername] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success('Welcome back!');
      navigate('/grid');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regEmail || !regUsername || !regPassword || !regConfirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (regPassword !== regConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (regPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (regUsername.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    
    setIsLoading(true);
    try {
      await register(regEmail, regPassword, regUsername, true);
      toast.success('Account created successfully!');
      navigate('/grid');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="relative z-10 w-full max-w-md">
        {/* Back Button */}
        <button 
          data-testid="back-to-landing-btn"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>

        {/* Auth Card */}
        <div className="glass rounded-xl p-6 md:p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#FF2E63] mb-1">PULSE</h1>
            <p className="text-[#A1A1AA] text-sm">Connect with people nearby</p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-white/5 mb-6">
              <TabsTrigger 
                data-testid="login-tab"
                value="login" 
                className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white"
              >
                Login
              </TabsTrigger>
              <TabsTrigger 
                data-testid="register-tab"
                value="register"
                className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white"
              >
                Register
              </TabsTrigger>
            </TabsList>

            {/* Login Form */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-[#EDEDED]">Email</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 focus:border-[#FF2E63]/50 focus:ring-[#FF2E63]/50"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-[#EDEDED]">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 pr-10 focus:border-[#FF2E63]/50 focus:ring-[#FF2E63]/50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit"
                  data-testid="login-submit-btn"
                  disabled={isLoading}
                  className="w-full py-6 font-bold uppercase tracking-wider bg-[#FF2E63] hover:bg-[#E01E50] text-white rounded-full btn-glow mt-6"
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
                
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-[#A1A1AA] hover:text-[#FF2E63] transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            </TabsContent>

            {/* Register Form */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reg-email" className="text-[#EDEDED]">Email</Label>
                  <Input
                    id="reg-email"
                    data-testid="register-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 focus:border-[#FF2E63]/50 focus:ring-[#FF2E63]/50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reg-username" className="text-[#EDEDED]">Username</Label>
                  <Input
                    id="reg-username"
                    data-testid="register-username-input"
                    type="text"
                    placeholder="Choose a username"
                    value={regUsername}
                    onChange={(e) => setRegUsername(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 focus:border-[#FF2E63]/50 focus:ring-[#FF2E63]/50"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="reg-password" className="text-[#EDEDED]">Password</Label>
                  <div className="relative">
                    <Input
                      id="reg-password"
                      data-testid="register-password-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min 6 characters"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 pr-10 focus:border-[#FF2E63]/50 focus:ring-[#FF2E63]/50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reg-confirm" className="text-[#EDEDED]">Confirm Password</Label>
                  <Input
                    id="reg-confirm"
                    data-testid="register-confirm-password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={regConfirmPassword}
                    onChange={(e) => setRegConfirmPassword(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 focus:border-[#FF2E63]/50 focus:ring-[#FF2E63]/50"
                  />
                </div>

                <Button 
                  type="submit"
                  data-testid="register-submit-btn"
                  disabled={isLoading}
                  className="w-full py-6 font-bold uppercase tracking-wider bg-[#FF2E63] hover:bg-[#E01E50] text-white rounded-full btn-glow mt-6"
                >
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </Button>
                
                <p className="text-xs text-[#52525B] text-center mt-4">
                  By creating an account, you agree to our{' '}
                  <Link to="/terms" className="text-[#FF2E63] hover:underline">Terms</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-[#FF2E63] hover:underline">Privacy Policy</Link>
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer Links */}
        <div className="flex justify-center gap-6 text-sm text-[#52525B] mt-6">
          <Link to="/terms" className="hover:text-[#A1A1AA] transition-colors">Terms</Link>
          <Link to="/privacy" className="hover:text-[#A1A1AA] transition-colors">Privacy</Link>
          <Link to="/faq" className="hover:text-[#A1A1AA] transition-colors flex items-center gap-1">
            <HelpCircle className="w-4 h-4" />
            Help
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Auth;
